import {useMemo, useRef} from 'react';

/**
 * Smooths over transient gaps in paginated list queries.
 *
 * Rapid page/filter changes can break the `keepPreviousData` chain, leaving
 * `data` briefly undefined mid-fetch — this serves the last good payload
 * during that gap; settled results always pass through. It also holds the
 * last known `totalRecords` when a later page omits it, resetting only when
 * a genuinely empty result comes back.
 *
 * @param {Object} parsedData - Formatted query result for the current page.
 * @param {boolean} isFetching - Whether the query is currently fetching.
 */
export const useStableListQueryData = (parsedData, isFetching) => {
  const lastGoodDataRef = useRef(null);
  const lastDataWithRowsRef = useRef(null);
  const data = useMemo(() => {
    const hasRows = parsedData?.data?.length > 0;
    if (hasRows || !isFetching) {
      lastGoodDataRef.current = parsedData;
      if (hasRows) {
        lastDataWithRowsRef.current = parsedData;
      }
      return parsedData;
    }
    // Fetching with no rows — serve the best stale data available.
    // lastGoodDataRef may have been overwritten with an empty settled result
    // (e.g. a filter that returned zero rows). In that case, fall back to
    // lastDataWithRowsRef so the table shows the previous good data during
    // the refetch instead of flashing the empty state again.
    if (lastGoodDataRef.current?.data?.length > 0) {
      return lastGoodDataRef.current;
    }
    return lastDataWithRowsRef.current ?? parsedData;
  }, [parsedData, isFetching]);

  const lastKnownTotalRef = useRef(0);
  const totalRecords = useMemo(() => {
    const parsed = Number(data?.totalRecords);
    if (Number.isFinite(parsed) && parsed > 0) {
      lastKnownTotalRef.current = parsed;
      return parsed;
    }
    return data?.data?.length > 0 ? lastKnownTotalRef.current : 0;
  }, [data]);

  return {data, totalRecords};
};
